import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip
} from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "common/pageLoader";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeReimbursementSubheadStatus, fetchReimbursementHeads, fetchReimbursementSubheads, saveReimbursementSubhead } from "store/actions";
import Swal from "sweetalert2";

const MasterSubHeads = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    masterheads,
    mastersubheads,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    masterheads: state.ReimbursementMasterReducer?.masterheads ?? [],
    mastersubheads: state.ReimbursementMasterReducer?.mastersubheads ?? [],

    loading: state.ReimbursementMasterReducer?.loading,
    error: state.ReimbursementMasterReducer?.error,
    success: state.ReimbursementMasterReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id;
    const userId = authUserDetails[0].user_id;
    dispatch(fetchReimbursementHeads(unitId, userId));
    dispatch(fetchReimbursementSubheads(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchReimbursementSubheads(authDetails?.unit_id));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      headsName: "",
      subHeadsName: "",
    },
    validationSchema: Yup.object({
      headsName: Yup.object().required("Please Enter Head Name"),
      subHeadsName: Yup.string().required("Please enter Sub Head Name"),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("subhead_id", editId ? editId : 0);
      formData.append("subheads_name", values.subHeadsName);
      formData.append("head_id", values.headsName?.value);
      formData.append("create_by", authDetails?.user_id);
      dispatch(saveReimbursementSubhead(formData));
    },
  });

  const handleEdit = (row) => {
    formik.setValues({
      headsName: { value: row.head_id, label: row.head_name },
      subHeadsName: row.subheads_name,
    });
    setIsEdit(true);
    setEditId(row.subhead_id);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEditId(null);
  };

  const columns = [
    {
      Header: "S.No",
      Cell: ({ row }) => mastersubheads.length - row.index,
      className: "serial-no",
      applyHeaderClass: true,
    },
    { Header: "Heads Name", accessor: "head_name", },
    { Header: "Sub Heads Name", accessor: "subheads_name", },
    {
      Header: "Action",
      className: "action-col",
      applyHeaderClass: true,
      Cell: (cellProps) => {
        const { b_active, subhead_id } = cellProps.row.original;
        const isActive = b_active === 1;
        return (
          <div className="d-flex gap-3">
            <Link
              to="#"
              style={{ color: isActive ? "green" : "red" }}
              onClick={() => handleToggleStatus(subhead_id, isActive)}
            >
              <i
                className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`}
                id={`status-tooltip-${cellProps.row.id}`}
              />
              <UncontrolledTooltip placement="top" target={`status-tooltip-${cellProps.row.id}`}>
                {isActive ? "Mark Inactive" : "Mark Active"}
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-success"
              onClick={() => handleEdit(cellProps.row.original)}
            >
              <i className="bx bxs-edit font-size-18" id="edittooltip" />
              <UncontrolledTooltip placement="top" target="edittooltip">
                Edit
              </UncontrolledTooltip>
            </Link>
          </div>
        );
      },
    },
  ];

  const handleToggleStatus = (subhead_id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("subhead_id", subhead_id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeReimbursementSubheadStatus(formData));
      }
    });
  };

  // select custome style
  const SelectcustomStyles = {
    control: (base) => ({
      ...base,
      maxheight: 33.1,
      minHeight: 33.1,
      // overflow: "scroll",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      padding: "0", // Match the padding for both dropdown and clear indicator
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "2px 8px", // Ensure consistent padding for dropdown indicator
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "2px 8px", // Adjust padding for clear indicator to match dropdown
    }),
  };


  return (
    <React.Fragment>
      <div className="page-content">
        <Loader loading={loading} />
        <Container fluid>
          <Breadcrumbs title="Reimbursement" breadcrumbItem="Master SubHeads" />

          {/* Input Section */}
          <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row className="mb-3 align-items-center">
                  <Col md="4">
                    <FormGroup>
                      <Label for="headsName">
                        Heads Name <span className="text-danger">*</span>
                      </Label>
                      <Select
                        styles={SelectcustomStyles}
                        id="headsName"
                        name="headsName"
                        // options={masterheads}
                        // value={masterheads.find(
                        //   (option) => option.value === formik.values.headsName
                        // )}
                        // onChange={(selectedOption) =>
                        //   formik.setFieldValue("headsName", selectedOption.value)
                        // }
                        // onBlur={formik.handleBlur}
                        // classNamePrefix="react-select"
                        options={(masterheads || []).map(emp => ({ value: emp.head_id, label: emp.head_name }))}
                        value={formik.values.headsName || null}
                        onChange={(selectedOption) => formik.setFieldValue("headsName", selectedOption)}
                        className="hrms-select"
                        classNamePrefix="hrms-select"
                      />
                      {formik.touched.headsName && formik.errors.headsName && (
                        <div className="text-danger">{formik.errors.headsName}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for="subHeadsName">
                        Sub Heads Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="subHeadsName"
                        name="subHeadsName"
                        type="text"
                        value={formik.values.subHeadsName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.subHeadsName &&
                          Boolean(formik.errors.subHeadsName)
                        }
                      />
                      {formik.touched.subHeadsName && formik.errors.subHeadsName && (
                        <div className="text-danger">{formik.errors.subHeadsName}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="3" className="d-flex mt-2 mob-center">
                    <Button type="submit" className="btn button-save me-2 flex-shrink-0">
                      <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                    </Button>
                    <Button
                      type="button"
                      onClick={formik.handleReset}
                      className="btn button-cancel flex-shrink-0"> <i className="fas fa-sync me-1" /> Cancel
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>

          </Card>

          {/* Table Section */}
          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={mastersubheads && mastersubheads.length > 0 ? mastersubheads : []}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MasterSubHeads;
